import React, { useState, useEffect } from 'react';
import { 
  Mail, Server, Lock, Shield, Send, CheckCircle, XCircle, 
  Loader2, Plus, Trash2, Eye, EyeOff, RefreshCw, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { adminEmailAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionEmail = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);

  const [form, setForm] = useState({
    nombre: 'Principal',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    email_from: '',
    nombre_from: 'LEXDUO'
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminEmailAPI.getConfigs();
      setConfigs(response.data);
    } catch (error) {
      console.error('Error cargando configuraciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      nombre: 'Principal',
      smtp_host: '',
      smtp_port: 587,
      smtp_user: '',
      smtp_password: '',
      smtp_use_tls: true,
      smtp_use_ssl: false,
      email_from: '',
      nombre_from: 'LEXDUO'
    });
    setEditingConfig(null);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setForm({
      nombre: config.nombre,
      smtp_host: config.smtp_host,
      smtp_port: config.smtp_port,
      smtp_user: config.smtp_user,
      smtp_password: '', // No mostrar password existente
      smtp_use_tls: config.smtp_use_tls,
      smtp_use_ssl: config.smtp_use_ssl,
      email_from: config.email_from,
      nombre_from: config.nombre_from
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingConfig) {
        await adminEmailAPI.update(editingConfig.id, form);
        toast({ title: "Configuración actualizada" });
      } else {
        await adminEmailAPI.create(form);
        toast({ title: "Configuración creada" });
      }
      setIsDialogOpen(false);
      resetForm();
      loadConfigs();
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo guardar la configuración",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (configId) => {
    setTesting(true);
    try {
      const response = await adminEmailAPI.test(configId);
      if (response.data.success) {
        toast({ title: "✅ Conexión exitosa", description: "La configuración SMTP es correcta" });
        loadConfigs();
      } else {
        toast({ 
          title: "❌ Error de conexión", 
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Error al probar la conexión",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !selectedConfigId) return;
    
    setTesting(true);
    try {
      const response = await adminEmailAPI.sendTest(selectedConfigId, testEmail);
      if (response.data.success) {
        toast({ title: "✅ Email enviado", description: `Se ha enviado un email de prueba a ${testEmail}` });
        setIsTestDialogOpen(false);
        setTestEmail('');
      } else {
        toast({ 
          title: "❌ Error", 
          description: response.data.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Error al enviar el email",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('¿Eliminar esta configuración de email?')) return;
    
    try {
      await adminEmailAPI.delete(configId);
      toast({ title: "Configuración eliminada" });
      loadConfigs();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6" /> Configuración de Email
          </h1>
          <p className="text-gray-600">Configura el servidor SMTP para enviar emails de alertas</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva configuración
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Server className="w-5 h-5 text-blue-600 mt-1" />
            <div>
              <h4 className="font-medium text-blue-800">Configuración SMTP</h4>
              <p className="text-sm text-blue-700 mt-1">
                Introduce los datos del servidor SMTP de tu proveedor de hosting (Plesk).
                Generalmente puedes encontrar estos datos en el panel de control de tu hosting.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de configuraciones */}
      {configs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Sin configuración</h3>
            <p className="text-gray-500 mt-1">Añade una configuración SMTP para poder enviar emails</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {configs.map(config => (
            <Card key={config.id} className={config.activo ? 'border-green-300' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config.activo ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Mail className={`w-6 h-6 ${config.activo ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{config.nombre}</h3>
                        {config.activo && <Badge className="bg-green-100 text-green-700">Activa</Badge>}
                        {config.verificado ? (
                          <Badge className="bg-blue-100 text-blue-700">
                            <CheckCircle className="w-3 h-3 mr-1" /> Verificada
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Sin verificar
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-600 space-y-1">
                        <p><strong>Servidor:</strong> {config.smtp_host}:{config.smtp_port}</p>
                        <p><strong>Usuario:</strong> {config.smtp_user}</p>
                        <p><strong>Remitente:</strong> {config.nombre_from} &lt;{config.email_from}&gt;</p>
                        <p><strong>Seguridad:</strong> {config.smtp_use_tls ? 'TLS' : config.smtp_use_ssl ? 'SSL' : 'Ninguna'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleTest(config.id)} disabled={testing}>
                      {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      <span className="ml-1">Probar</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => { setSelectedConfigId(config.id); setIsTestDialogOpen(true); }}
                      disabled={!config.verificado}
                    >
                      <Send className="w-4 h-4 mr-1" /> Enviar test
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(config)}>
                      Editar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingConfig ? 'Editar' : 'Nueva'} configuración SMTP</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nombre de la configuración</Label>
              <Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} required />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Servidor SMTP *</Label>
                <Input 
                  value={form.smtp_host} 
                  onChange={(e) => setForm({...form, smtp_host: e.target.value})} 
                  placeholder="mail.tudominio.com"
                  required 
                />
              </div>
              <div>
                <Label>Puerto *</Label>
                <Input 
                  type="number" 
                  value={form.smtp_port} 
                  onChange={(e) => setForm({...form, smtp_port: parseInt(e.target.value)})} 
                  required 
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Usuario SMTP *</Label>
                <Input 
                  value={form.smtp_user} 
                  onChange={(e) => setForm({...form, smtp_user: e.target.value})} 
                  placeholder="usuario@tudominio.com"
                  required 
                />
              </div>
              <div>
                <Label>Contraseña SMTP *</Label>
                <div className="relative">
                  <Input 
                    type={showPassword ? 'text' : 'password'}
                    value={form.smtp_password} 
                    onChange={(e) => setForm({...form, smtp_password: e.target.value})} 
                    placeholder={editingConfig ? '(sin cambios)' : ''}
                    required={!editingConfig}
                  />
                  <button 
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setForm({...form, smtp_use_tls: v, smtp_use_ssl: v ? false : form.smtp_use_ssl})} />
                <Label>Usar TLS</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.smtp_use_ssl} onCheckedChange={(v) => setForm({...form, smtp_use_ssl: v, smtp_use_tls: v ? false : form.smtp_use_tls})} />
                <Label>Usar SSL</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email remitente *</Label>
                <Input 
                  type="email"
                  value={form.email_from} 
                  onChange={(e) => setForm({...form, email_from: e.target.value})} 
                  placeholder="noreply@tudominio.com"
                  required 
                />
              </div>
              <div>
                <Label>Nombre remitente</Label>
                <Input 
                  value={form.nombre_from} 
                  onChange={(e) => setForm({...form, nombre_from: e.target.value})} 
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog enviar test */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar email de prueba</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email destinatario</Label>
              <Input 
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSendTestEmail} disabled={testing || !testEmail} className="bg-blue-600 hover:bg-blue-700">
              {testing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Send className="w-4 h-4 mr-2" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracionEmail;
